import { Inject, Injectable, Optional } from '@angular/core';
import { Shipment } from '../../../models/shipment';
import { ConstraintEnum } from '../../../enum/constraint.enum';
import { Violation } from '../../../models/violation';
import { cardLimits } from '../../../constants/card-limits';
import { CardTypeEnum } from '../../../enum/card-type.enum';
import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { shipmentConstraints } from '../../../constants/shipment-constraints';
import { Clock } from '../../../utils/clock';
import { CountriesService } from './countries.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { shipmentEditorSetViolations } from '../../../store/shipment-editor/shipment-editor.actions';
import { propertyWalker } from '../../../utils/property-walker';
import { STATE_VALIDATORS } from '../../../constants/state-validators';
import { StateValidator } from '../../../interfaces/state-validator';
import { selectShipmentEditorShipment } from '../../../store/shipment-editor/shipment-editor.state';
import { ValidationMode } from '../../../enum/validation-mode.enum';
import { ConstraintTypeEnum } from '../../../enum/constraint-type.enum';

@Injectable({
    providedIn: 'root'
})
export class ShipmentValidatorService {

    constructor(@Optional() @Inject(STATE_VALIDATORS) private validators: StateValidator[],
                private countriesService: CountriesService, private store: Store<AppState>) {
    }

    private static isQuotingConstraint(id: ConstraintEnum): boolean {
        return [ ConstraintEnum.PACKAGE_WEIGHT_ZERO ].indexOf(id) >= 0;
    }

    public static forCard(id: ConstraintEnum): CardTypeEnum {
        if (id.indexOf('SHIPPER') === 0) {
            return CardTypeEnum.SHIPPER;
        }
        if (id.indexOf('DESTINATION') === 0) {
            return CardTypeEnum.DESTINATION;
        }
        return null;
    }

    public static errorName(id: ConstraintEnum): string {
        return id.split('_')[2].toLocaleLowerCase();
    }

    public static fieldName(id: ConstraintEnum): string {
        return id.split('_')[1].toLocaleLowerCase();
    }

    private static test(shipment: Shipment, violation: ConstraintEnum): string {
        switch (violation) {
            case ConstraintEnum.SHIPPER_ATTN_EMPTY:
                return shipment.shipper.attn.trim().length === 0 ?
                    $localize`You must provide the contact name of the shipper` : '';
            case ConstraintEnum.SHIPPER_ORGANIZATION_EMPTY:
                return shipment.shipper.organization.trim().length === 0 ?
                    $localize`You must provide the name of the shipper` : '';
            case ConstraintEnum.SHIPPER_STREET1_EMPTY:
                return shipment.shipper.street1.trim().length === 0 ?
                    $localize`You must provide the first line of the street address` : '';
            case ConstraintEnum.SHIPPER_STREET1_LENGTH:
                return shipment.shipper.street1.length > cardLimits.street1.characters ?
                    $localize`The length of the street address cannot exceed ${cardLimits.street1.characters}` : '';
            case ConstraintEnum.SHIPPER_STREET2_LENGTH:
                return shipment.shipper.street2.length > cardLimits.street2.characters ?
                    $localize`The length of the second street address cannot exceed ${cardLimits.street2.characters}` : '';
            case ConstraintEnum.SHIPPER_TELEPHONE_EMPTY:
                return shipment.shipper.telephone.trim().length === 0 ?
                    $localize`You must provide a shipper telephone number` : '';
            case ConstraintEnum.SHIPPER_COUNTRY_EMPTY:
                return shipment.shipper.country.length === 0 ?
                    $localize`You must select a shipper country` : '';
            case ConstraintEnum.DESTINATION_COUNTRY_EMPTY:
                return shipment.destination.country.length === 0 ?
                    $localize`You must select a destination country` : '';
            case ConstraintEnum.SHIPMENT_DATE_PAST:
                return shipment.shipmentDate < Clock.now() ?
                    $localize`Shipping Date must be today or in the future` : '';
            default:
                return '';
        }
    }

    private static matchPaths(a: string, b: string): boolean {
        const aa = a.split('.');
        const bb = b.split('.');
        if (aa.length === bb.length) {
            for (let i = 0; i < aa.length; i++) {
                if (aa[i] !== bb[i] && aa[i] !== '*' && bb[i] !== '*') {
                    return false;
                }
            }
            return true;
        } else {
            return false;
        }
    }

    public validateShipperForQuoting(shipment: Shipment): string {
        if (!shipment.shipper.country) {
            return $localize`You must select a shipper country`;
        }
        if (this.countriesService.displayPostalCodesFor(shipment.shipper.country)) {
            if (!shipment.shipper.postalCode || shipment.shipper.postalCode.trim().length === 0) {
                return $localize`You must provide a shipper postal code`;
            }
        } else {
            if (!shipment.shipper.city || shipment.shipper.city.trim().length === 0) {
                return $localize`You must provide a shipper city`;
            }
        }
        return null;
    }

    public validateDestinationForQuoting(shipment: Shipment): string {
        if (!shipment.destination.country) {
            return $localize`You must select a destination country`;
        }
        if (this.countriesService.displayPostalCodesFor(shipment.destination.country)) {
            if (!shipment.destination.postalCode || shipment.destination.postalCode.trim().length === 0) {
                return $localize`You must provide a destination postal code`;
            }
        } else {
            if (!shipment.destination.city || shipment.destination.city.trim().length === 0) {
                return $localize`You must provide a destination city`;
            }
        }
        return null;
    }

    public validateSoldToForQuoting(shipment: Shipment): string {
        if (!shipment.soldTo.country) {
            return $localize`You must select a sold-to country`;
        }
    }


    public validateBrokerForQuoting(shipment: Shipment): string {
        if (!shipment.broker.country) {
            return $localize`You must select a broker country`;
        }
    }

    private getValidators(): StateValidator[] {
        return this.validators ?? [];
    }

    public validateEditorShipment(shipment: Shipment, mode: ValidationMode): Violation[] {
        if (!shipment) {
            return [];
        }
        const violations = [];
        const validators = this.getValidators();
        validators.forEach(validator => {
            if (validator.supports(ConstraintTypeEnum.SHIPMENT)) {
                validator.validate(shipment, ConstraintTypeEnum.SHIPMENT, mode)
                    .forEach(violation => violations.push(violation));
            }
            if (validator.supports(ConstraintTypeEnum.PACKAGE)) {
                shipment.packages.forEach((pkg, index) => {
                    validator.validate(pkg, ConstraintTypeEnum.PACKAGE, mode)
                        .forEach(violation => violations.push({
                            ...violation,
                            path: `shipment.package.${index}.${violation.path}`
                        }));
                });
            }
            if (validator.supports(ConstraintTypeEnum.COMMODITY)) {
                shipment.packages.forEach((pkg, pkgIndex) => {
                    pkg.commodities.forEach((commodity, commodityIndex) => {
                        validator.validate(commodity, ConstraintTypeEnum.COMMODITY, mode)
                            .forEach(violation => violations.push({
                                ...violation,
                                path: `shipment.package.${pkgIndex}.commodity.${commodityIndex}.${violation.path}`
                            }));
                    });
                });
            }
        });
        return violations;
    }
}
